import { format, getISOWeek, isSameDay } from 'date-fns';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DayGroup } from './dayGroups';

interface DayGroupHeaderProps {
  group: DayGroup;
  today: Date;
  /** Hide the `· W40` suffix when the surrounding section already shows it. */
  showWeek?: boolean;
}

export function DayGroupHeader({
  group,
  today,
  showWeek = true,
}: DayGroupHeaderProps) {
  if (!group.date) {
    return (
      <div
        className="border-muted-foreground/30 flex items-center gap-2 border-l-4 pl-3 text-sm
          font-semibold"
      >
        Unscheduled
      </div>
    );
  }

  const weekNo = getISOWeek(group.date);
  const isToday = isSameDay(group.date, today);
  const dayLabel = format(group.date, 'EEE d MMM');

  return (
    <div
      className={cn(
        'flex items-center gap-2 border-l-4 pl-3 text-sm font-semibold',
        group.isCarryOver
          ? 'border-amber-500 text-amber-700 dark:text-amber-300'
          : isToday
            ? 'border-primary'
            : 'border-muted-foreground/30',
      )}
    >
      {group.isCarryOver && (
        <AlertTriangle
          className="h-4 w-4 text-amber-600 dark:text-amber-400"
          aria-hidden="true"
        />
      )}
      <span>{dayLabel}</span>
      {showWeek && (
        <span className="text-muted-foreground text-xs font-normal">
          · W{weekNo}
        </span>
      )}
      {group.isCarryOver && (
        <span
          className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800
            dark:bg-amber-950/40 dark:text-amber-300"
        >
          Carry-over
        </span>
      )}
      {isToday && !group.isCarryOver && (
        <span className="text-primary ml-1 text-xs font-normal">Today</span>
      )}
    </div>
  );
}
